import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/electron/renderer'
import { useAtomValue, useSetAtom } from 'jotai'
import { Archive, CheckCheck } from 'lucide-react'

import { Button, Tooltip } from '@highlight/ui'
import { isAnswerableStagedAction, isPendingAnswerableStagedAction } from '@renderer/chat/integrations/answerable'
import { isActionRunInItsChat } from '@renderer/chat/integrations/conversation-action'
import { isReportToUserStagedAction } from '@renderer/chat/integrations/report-to-user'
import { isNativeTextEntryTarget } from '@renderer/utils/is-interactive-focused'
import { ActionViewDirtyControls } from '../action-view/nested/dirty-controls'
import { PendingAnswerableCard } from '../../components/pending-answerable-card'
import {
  isEditableActionStatus,
  useStagedActionEditOptions,
  useArchiveStagedActions,
  useStagedActionDerivations,
  useStagedActionInvoke,
  useVisibleStagedActions,
  type StagedAction,
} from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { actionInlineDraftsAtom } from '../action-view/inline-draft-state'
import { requiresExplicitSave } from '../action-view/inline-drafts'
import { useActionInlineDraftController } from '../action-view/use-action-inline-draft-controller'
import { ActionViewFloatingBarSlot, FloatingBarRegionSwap } from '../action-view/nested/floating-controls'
import { ActionViewChatCircle, ActionViewChatCircleNeighbours } from '../action-view/nested/chat-control'
import { resolveActionSpanLink } from '../../components/braintrust-links'
import { FLOATING_FILL_CLASS } from '@renderer/components/floating-surface'
import { FeedbackMenuButton } from '../../components/feedback-menu-button'
import { feedbackSubject } from '@renderer/chat/api/feedback'
import { useUpdateTopViewContext } from '../../view-atoms'
import {
  answerableStepCount,
  executeStagedActionsSequentially,
  orderStagedActionsChronologically,
  resolveChatOutputFloatingRegion,
} from './chat-output'
import type { AnswerableProgress } from '@renderer/chat/integrations/answerable'
import { optimisticQuestionAnswersAtom } from './question-progress-state'
import { savedDraftFocusResetAtom } from './saved-draft-state'
import { useIsChatOutputStreaming } from './use-chat-output-view-model'

type TitleCommitter = () => Promise<boolean>

/** Gives the batch footer the same pre-execute title flush as an individual action bar. */
function BatchActionTitleCommitter({
  action,
  register,
}: {
  action: StagedAction
  register: (actionId: string, commitTitle: TitleCommitter | null) => void
}) {
  const { editOptions } = useStagedActionEditOptions({ actionId: action.id })
  const { commitTitle } = useActionInlineDraftController({
    actionId: action.id,
    coreTitle: editOptions?.title?.coreTitle,
    serverContent: action.view?.content,
  })

  useEffect(() => {
    register(action.id, commitTitle)
    return () => register(action.id, null)
  }, [action.id, commitTitle, register])

  return null
}

export function ChatOutputFloatingBarSlot({
  actionIds,
  conversationId,
  focusedActionId,
  chatOpen = false,
}: {
  actionIds: ReadonlyArray<string>
  /** The output's conversation, whose turn liveness the busy pill reports. */
  conversationId?: string
  focusedActionId?: string
  chatOpen?: boolean
}) {
  const { allActions } = useVisibleStagedActions()
  const { getActionStatus } = useStagedActionDerivations()
  const { invokeStagedAction, invokingId } = useStagedActionInvoke()
  const { bulkArchiveStagedActions } = useArchiveStagedActions()
  const isStreaming = useIsChatOutputStreaming(conversationId)
  const updateOutputContext = useUpdateTopViewContext('chatOutput')
  const drafts = useAtomValue(actionInlineDraftsAtom)
  const setOptimisticQuestionAnswers = useSetAtom(optimisticQuestionAnswersAtom)
  const setSavedDraftFocusReset = useSetAtom(savedDraftFocusResetAtom)
  const [isExecuting, setIsExecuting] = useState(false)
  const [isArchiving, setIsArchiving] = useState(false)
  const titleCommittersRef = useRef(new Map<string, TitleCommitter>())
  const registerTitleCommitter = useCallback((actionId: string, commitTitle: TitleCommitter | null) => {
    if (commitTitle) {
      titleCommittersRef.current.set(actionId, commitTitle)
    } else {
      titleCommittersRef.current.delete(actionId)
    }
  }, [])

  const actions = useMemo(() => {
    const requestedIds = new Set(actionIds)
    // The view may have opened with the id of an action this chat is running. The chat already shows that work.
    return orderStagedActionsChronologically(
      allActions.filter((action) => requestedIds.has(action.id) && !isActionRunInItsChat(action)),
    )
  }, [actionIds, allActions])

  const executableActions = actions.filter((action) => {
    const status = getActionStatus(action)
    return (
      isEditableActionStatus(status) &&
      !action.view?.invokeLink &&
      !isAnswerableStagedAction(action) &&
      !isReportToUserStagedAction(action)
    )
  })
  const pendingQuestions = actions.flatMap((action) => {
    if (!isPendingAnswerableStagedAction(action)) {
      return []
    }
    const questionCount = answerableStepCount(action)
    return questionCount !== null ? [{ action, questionCount }] : []
  })
  const questionSessionRef = useRef(new Map<string, number>())
  // Membership in the batch, not liveness: an ask that has been resolved *or*
  // dismissed keeps its steps in the total for the rest of the session, so
  // finishing "Question 2 of 3" can't relabel the next card "1 of 2".
  const questionActionIds = new Set(
    actions.filter((action) => isAnswerableStagedAction(action)).map((action) => action.id),
  )
  for (const actionId of questionSessionRef.current.keys()) {
    if (!questionActionIds.has(actionId)) {
      questionSessionRef.current.delete(actionId)
    }
  }
  for (const { action, questionCount } of pendingQuestions) {
    if (!questionSessionRef.current.has(action.id)) {
      questionSessionRef.current.set(action.id, questionCount)
    }
  }
  if (pendingQuestions.length === 0) {
    questionSessionRef.current.clear()
  }

  const pendingQuestion = pendingQuestions[0]
  const pendingQuestionId = pendingQuestion?.action.id
  const questionEntries = [...questionSessionRef.current.entries()]
  const questionOffset = pendingQuestion
    ? questionEntries
        .slice(
          0,
          questionEntries.findIndex(([actionId]) => actionId === pendingQuestion.action.id),
        )
        .reduce((total, [, count]) => total + count, 0)
    : 0
  const questionTotal = questionEntries.reduce((total, [, count]) => total + count, 0)
  const singleActionStatus = actions.length === 1 ? getActionStatus(actions[0]) : undefined
  const singleActionHasInlineCompletedControls =
    singleActionStatus === 'pending' || singleActionStatus === 'completed' || singleActionStatus === 'undo-error'
  const selectedActionHasOwnToolbar =
    singleActionHasInlineCompletedControls ||
    (actions.length > 1 && actions.some((action) => action.id === focusedActionId && !isAnswerableStagedAction(action)))
  const region = resolveChatOutputFloatingRegion({
    hasPendingQuestions: pendingQuestion !== undefined,
    actionCount: actionIds.length,
    selectedActionHasOwnToolbar,
    isStreaming,
  })
  const hasUnsavedChanges = executableActions.some((action) => requiresExplicitSave(drafts[action.id]))
  const focusedDirtyAction = actions.find(
    (action) => action.id === focusedActionId && requiresExplicitSave(drafts[action.id]),
  )
  // Only this batch's own invoke makes it busy, not one running elsewhere.
  const isInvoking = invokingId !== null && actionIds.includes(invokingId)
  const isBusy = isExecuting || isInvoking || isArchiving
  const feedback = executableActions.map(feedbackSubject).find((subject) => subject !== undefined)
  const braintrustLink = executableActions
    .map((action) => resolveActionSpanLink(action))
    .find((link) => link !== undefined)

  const openChat = useCallback(() => {
    updateOutputContext((current) => (current && !current.chat ? { ...current, chat: true } : current))
  }, [updateOutputContext])

  const archiveAll = useCallback(() => {
    if (isBusy || executableActions.length === 0) return

    const run = async () => {
      setIsArchiving(true)
      try {
        await bulkArchiveStagedActions(executableActions.map((action) => action.id))
      } catch (error) {
        Sentry.captureException(error, {
          tags: { area: 'chat-output', operation: 'archive-all-actions' },
          extra: { actionIds: executableActions.map((action) => action.id) },
        })
      } finally {
        setIsArchiving(false)
      }
    }
    void run()
  }, [bulkArchiveStagedActions, executableActions, isBusy])

  const recordQuestionProgress = useCallback(
    (answer: AnswerableProgress) => {
      if (!pendingQuestionId) {
        return
      }
      setOptimisticQuestionAnswers((current) => ({ ...current, [pendingQuestionId]: answer }))
    },
    [pendingQuestionId, setOptimisticQuestionAnswers],
  )

  useEffect(() => {
    const pendingIds = new Set(pendingQuestions.map(({ action }) => action.id))
    setOptimisticQuestionAnswers((current) => {
      const entries = Object.entries(current).filter(([actionId]) => pendingIds.has(actionId))
      return entries.length === Object.keys(current).length ? current : Object.fromEntries(entries)
    })
  }, [pendingQuestions, setOptimisticQuestionAnswers])

  useEffect(
    () => () => {
      setOptimisticQuestionAnswers({})
    },
    [setOptimisticQuestionAnswers],
  )

  const executeAll = useCallback(() => {
    if (isBusy || hasUnsavedChanges || executableActions.length === 0) return

    const run = async () => {
      setIsExecuting(true)
      try {
        await executeStagedActionsSequentially(
          executableActions.map((action) => action.id),
          async (actionId) => {
            const titleCommitted = await (titleCommittersRef.current.get(actionId)?.() ?? true)
            if (titleCommitted) await invokeStagedAction(actionId)
          },
          (error, actionId) =>
            Sentry.captureException(error, {
              tags: { area: 'chat-output', operation: 'execute-all-actions' },
              extra: { actionId },
            }),
        )
      } finally {
        setIsExecuting(false)
      }
    }
    void run()
  }, [executableActions, hasUnsavedChanges, invokeStagedAction, isBusy])

  useEffect(() => {
    if (isBusy || hasUnsavedChanges || executableActions.length === 0) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key !== 'Enter' || isNativeTextEntryTarget(event.target)) return
      event.preventDefault()
      event.stopPropagation()
      executeAll()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [executeAll, executableActions.length, hasUnsavedChanges, isBusy])

  // One key per bar owner — a key change drives the swap choreography (the
  // outgoing region blur-drops and the incoming one rises, same as the Action
  // View bar). The single-action toolbar resolves its own regions — dirty
  // included — behind this key, so a focused edit swaps inside it instead of
  // double-animating here.
  const regionKey =
    region === 'questions' && pendingQuestion
      ? `question:${pendingQuestion.action.id}`
      : region === 'single-action-toolbar'
        ? 'single-action-toolbar'
        : focusedDirtyAction
          ? 'dirty'
          : region === 'batch-toolbar' && executableActions.length > 0
            ? 'batch-toolbar'
            : isStreaming
              ? 'working'
              : null

  // The registry cannot read turn liveness, so this stays mounted until both the action list and stream are empty.
  if (actionIds.length === 0 && !isStreaming) {
    return null
  }

  return (
    <FloatingBarRegionSwap regionKey={regionKey}>
      {/* A blocking question owns the floating region until it is answered. The
          answer mutation updates outputData optimistically, which removes this
          card immediately and reveals the normal batch controls beneath it. */}
      {region === 'questions' && pendingQuestion ? (
        <PendingAnswerableCard
          action={pendingQuestion.action}
          source="chat-output"
          progress={{
            offset: questionOffset,
            total: questionTotal,
            hasFollowingSteps: questionOffset + pendingQuestion.questionCount < questionTotal,
          }}
          onProgress={recordQuestionProgress}
        />
      ) : region === 'single-action-toolbar' ? (
        <ActionViewFloatingBarSlot
          actionId={actionIds[0]}
          showAskUserCards={false}
          onDraftSaved={() => setSavedDraftFocusReset({ actionId: actionIds[0] })}
          chatOpen={chatOpen}
          onOpenChat={openChat}
          applyingChanges={isStreaming}
        />
      ) : focusedDirtyAction ? (
        <ActionViewDirtyControls
          action={focusedDirtyAction}
          draft={drafts[focusedDirtyAction.id]}
          onSaved={() => setSavedDraftFocusReset({ actionId: focusedDirtyAction.id })}
        />
      ) : regionKey === 'batch-toolbar' ? (
        <Fragment>
          {executableActions.map((action) => (
            <BatchActionTitleCommitter key={action.id} action={action} register={registerTitleCommitter} />
          ))}
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
            {/* relative: the displaced controls exit with `popLayout`, which
                positions them absolutely against the nearest positioned box. */}
            <div className="relative flex shrink-0 items-center gap-2">
              {/* The turn is rewriting these drafts, so rating and archiving
                  them are premature until it settles. */}
              <ActionViewChatCircle open={chatOpen} onOpen={openChat} applyingChanges={isStreaming} />
              <ActionViewChatCircleNeighbours hidden={isStreaming}>
                {feedback && (
                  <FeedbackMenuButton
                    subject={feedback.subject}
                    currentRating={feedback.initialRating}
                    source="action"
                    braintrustLink={braintrustLink}
                    onArchive={archiveAll}
                  />
                )}
              </ActionViewChatCircleNeighbours>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <ActionViewChatCircleNeighbours hidden={isStreaming}>
                <Tooltip>
                  <Tooltip.Trigger
                    render={
                      <Button
                        variant="outline"
                        shape="square"
                        aria-label="Archive all actions"
                        onClick={archiveAll}
                        disabled={isBusy}
                        className={FLOATING_FILL_CLASS}
                      >
                        <Archive aria-hidden="true" />
                      </Button>
                    }
                  />
                  <Tooltip.Content>Archive</Tooltip.Content>
                </Tooltip>
              </ActionViewChatCircleNeighbours>
              <Button
                onClick={executeAll}
                variant="primary"
                className="shrink-0"
                disabled={isBusy || hasUnsavedChanges}
                loading={isExecuting || isInvoking}
                left={<CheckCheck aria-hidden="true" />}
                hotkey={!hasUnsavedChanges ? 'cmd + enter' : undefined}
              >
                Approve All
              </Button>
            </div>
          </div>
        </Fragment>
      ) : regionKey === 'working' ? (
        <ActionViewChatCircle open={chatOpen} onOpen={openChat} applyingChanges />
      ) : null}
    </FloatingBarRegionSwap>
  )
}
